// src/main/content/contests/useIsTruncated.js
//
// Detect whether an element with ``truncate`` (or ``line-clamp-N``)
// classes is actually clipping its content. Compares ``scrollWidth/Height``
// to ``clientWidth/Height`` and re-checks on resize via ``ResizeObserver``.
//
// Use this to gate tooltip wrapping so we only show "recover the full
// text" tooltips when there's something to recover — a tooltip that just
// echoes already-visible text is noise. Used by ``HeroStat`` in
// ``ContestDetails`` and the store-names line in ``ContestList`` to
// handle both "explicitly truncated via '+N more'" and "visually clipped
// by the column width" cases uniformly.

import { useEffect, useRef, useState } from "react";

export function useIsTruncated(dep, { multiLine = false } = {}) {
  const ref = useRef(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      setTruncated(false);
      return undefined;
    }
    const check = () => {
      // ``+1`` forgives sub-pixel rounding — browsers occasionally report
      // ``scrollWidth = clientWidth + 0.5`` on non-truncated content
      // depending on font hinting / zoom level.
      const overflows = multiLine
        ? el.scrollHeight > el.clientHeight + 1
        : el.scrollWidth > el.clientWidth + 1;
      setTruncated(overflows);
    };
    check();
    // ``ResizeObserver`` is missing in older Safari/WebView builds and in
    // jsdom test environments without a polyfill. Without the guard, this
    // hook would throw at render time and take down every page that uses
    // it (contest list + contest detail). Falling back to a one-shot
    // mount/dep check keeps the tooltip-on-truncation behavior correct
    // for static text — we just miss live updates on viewport resize,
    // which is an acceptable degradation for the small set of users on
    // very old browsers.
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [dep, multiLine]);

  return [ref, truncated];
}
